// web-frontend/src/shared/services/surveyService.js
/**
 * 🔥 설문 서비스 (API 연동)
 * - 온보딩 설문 조회/제출/확인
 * - 일반 설문 조회/제출
 * - device_id 기반 제출 이력 관리
 * - 🔥 [신규] 사용자 타입 기반 대기 설문 조회
 * 
 * 🔥🔥🔥 [2024-12 수정] question_id null 버그 수정
 * - 특수 키(location_*, meeting_schedule)를 적절한 question_id로 매핑
 * - 숫자가 아닌 키 필터링
 * 
 * 🔥🔥🔥 [2025-12-18 수정] submitSurvey 응답 데이터 반환 보장
 * - 백엔드에서 반환하는 carbon_points_earned, carbon_saved_kg, distance_km 포함
 */

import apiClient from './apiClient';
import deviceManager from '../utils/deviceManager';
import logger from '../utils/logger';

/**
 * 🔥🔥🔥 [핵심 수정] 프론트엔드 answers 객체를 백엔드 API 형식으로 변환
 * 
 * 문제: 
 * - location_country, location_region 등 문자열 키가 parseInt 시 NaN → null 변환
 * - 백엔드 AnswerSubmission.question_id는 int 필수 (null 불허)
 * 
 * 해결:
 * 1. 숫자 ID 키만 처리 (0, 1, 2, ...)
 * 2. 특수 키(location_*, meeting_schedule)는 필터링 (이미 숫자 ID에 저장됨)
 * 
 * @param {Object} answers - { questionId: answer } 형식
 * @returns {Array} 백엔드 형식의 답변 배열
 */
const transformAnswersForApi = (answers) => {
  const result = [];
  
  Object.entries(answers).forEach(([questionId, answer]) => {
    const parsedId = parseInt(questionId, 10);
    
    // 🔥 숫자로 변환 가능한 키만 처리 (NaN 제외)
    if (!isNaN(parsedId)) {
      // 빈 답변 필터링
      const answerValue = Array.isArray(answer) ? answer : [answer];
      const filteredValue = answerValue.filter(v => v !== null && v !== undefined && v !== '');
      
      // 답변이 있는 경우에만 추가
      if (filteredValue.length > 0) {
        result.push({
          question_id: parsedId,
          answer_value: filteredValue,
        });
      }
    }
    // 🔥 문자열 키(location_country, location_region, meeting_schedule 등)는 무시
    // → SurveyPage.jsx에서 이미 숫자 ID에 저장하도록 수정됨
  });
  
  // 🔥 디버그 로그
  logger.debug('🔄 transformAnswersForApi 결과:', result);
  
  return result;
};

const surveyService = {
  /**
   * 🔥🔥🔥 [신규] 대기 중인 설문 목록 조회 (사용자 타입 기반)
   * - 백엔드 /surveys/pending API 호출
   * - 사용자 타입에 맞는 설문만 반환됨
   * @returns {Promise<Array>} 대기 중인 설문 목록
   */
  getPendingSurveys: async () => {
    try {
      const deviceId = await deviceManager.getDeviceId();
      logger.debug('📋 대기 설문 조회 중... (device_id:', deviceId, ')');
      
      const data = await apiClient.get('/surveys/pending', {
        params: { device_id: deviceId },
        timeout: 10000
      });
      
      logger.debug('✅ 대기 설문 조회 완료:', data);
      return data || [];
      
    } catch (error) {
      logger.error('❌ 대기 설문 조회 실패:', error);
      
      // 에러 발생 시 빈 배열 반환 (폴백)
      return [];
    }
  },

  /**
   * 🔥🔥🔥 [신규] 사용자 타입에 맞는 첫 번째 대기 설문 조회
   * @param {string} userType - 사용자 타입 (BUYER, COMPANY, VISITOR 등)
   * @returns {Promise<Object|null>} 대기 설문 또는 null
   */
  getFirstPendingSurvey: async (userType) => {
    try {
      logger.debug('🔍 사용자 타입별 대기 설문 확인:', userType);
      
      const pendingSurveys = await surveyService.getPendingSurveys();
      
      if (!pendingSurveys || pendingSurveys.length === 0) {
        logger.debug('ℹ️ 대기 중인 설문 없음');
        return null;
      }
      
      // 🔥 PRE_LOGIN 타입 설문을 우선 반환 (초기 설문)
      const preLoginSurvey = pendingSurveys.find(
        s => s.type === 'pre_login' || s.type === 'PRE_LOGIN'
      );
      
      if (preLoginSurvey) {
        logger.debug('✅ PRE_LOGIN 설문 발견:', preLoginSurvey.slug || preLoginSurvey.id);
        return preLoginSurvey;
      }
      
      // 🔥 is_onboarding 설문 반환
      const onboardingSurvey = pendingSurveys.find(s => s.is_onboarding === true);
      
      if (onboardingSurvey) {
        logger.debug('✅ 온보딩 설문 발견:', onboardingSurvey.slug || onboardingSurvey.id);
        return onboardingSurvey;
      }
      
      // 그 외 첫 번째 설문 반환
      logger.debug('✅ 첫 번째 대기 설문 반환:', pendingSurveys[0].slug || pendingSurveys[0].id);
      return pendingSurveys[0];
      
    } catch (error) {
      logger.error('❌ 사용자 타입별 대기 설문 조회 실패:', error);
      return null;
    }
  },

  /**
   * 🔥🔥🔥 [신규] 사용자 타입에 따른 기본 설문 slug 결정
   * - pending API 실패 시 폴백으로 사용
   * @param {string} userType - 사용자 타입
   * @returns {string} 설문 slug
   */
  getDefaultSurveySlug: (userType) => {
    const userTypeLower = (userType || '').toLowerCase();
    
    const surveyMapping = {
      'buyer': 'buyer-initial-survey',
      'company': 'onboarding',      // 기업은 일반 온보딩
      'exhibitor': 'onboarding',    // 전시기업도 일반 온보딩
      'investor': 'onboarding',     // 투자자는 일반 온보딩
      'visitor': 'onboarding',      // 방문객은 일반 온보딩
      'business_visitor': 'onboarding',
      'admin': 'onboarding',
    };
    
    const slug = surveyMapping[userTypeLower] || 'onboarding';
    logger.debug('🎯 기본 설문 slug 결정: userType=' + userType + ' → slug=' + slug);
    
    return slug;
  },

  /**
   * 온보딩 설문 조회
   * @returns {Promise<Object>} 온보딩 설문 데이터
   */
  getOnboardingSurvey: async () => {
    try {
      logger.debug('📋 온보딩 설문 조회 중...');
      const data = await apiClient.get('/surveys/onboarding');
      logger.debug('✅ 온보딩 설문 조회 성공');
      return data;
    } catch (error) {
      logger.error('❌ 온보딩 설문 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 🔥 온보딩 설문 제출 확인 (참고사항 반영)
   * - deviceId를 내부에서 자동 생성
   * - 파라미터로 받지 않음
   * @returns {Promise<Object>} { has_submitted: boolean, can_submit_again?: boolean, last_submission?: string }
   */
  checkOnboardingSubmission: async () => {
    try {
      const deviceId = await deviceManager.getDeviceId();
      logger.debug('🔍 온보딩 설문 제출 이력 확인:', deviceId);
      
      // 백엔드가 device_id를 쿼리 파라미터로 받음
      const data = await apiClient.get('/surveys/onboarding/check', {
        params: { device_id: deviceId },
        timeout: 5000
      });
      
      // 🔥 응답 데이터 정규화 (여러 가능성 체크)
      const hasSubmitted = 
        data?.has_submitted === true || 
        data?.hasSubmitted === true || 
        data?.submitted === true || 
        false;
      
      const canSubmitAgain = 
        data?.can_submit_again !== false && 
        data?.canSubmitAgain !== false;
      
      const lastSubmission = 
        data?.last_submission || 
        data?.lastSubmission || 
        data?.submitted_at || 
        data?.submittedAt;
      
      const result = {
        has_submitted: hasSubmitted,
        can_submit_again: canSubmitAgain,
        last_submission: lastSubmission
      };
      
      logger.debug('✅ 설문 제출 이력 확인 완료:', result);
      return result;
      
    } catch (error) {
      logger.error('❌ 온보딩 설문 제출 이력 확인 실패:', error);
      
      // 🔥 HTTP 상태 코드별 처리
      if (error.response?.status === 404) {
        logger.debug('ℹ️ 404 - 설문 미제출 상태');
        return {
          has_submitted: false,
          can_submit_again: true
        };
      }
      
      if (error.response?.status === 400) {
        logger.debug('ℹ️ 400 - 설문 이미 제출됨');
        return {
          has_submitted: true,
          can_submit_again: false
        };
      }
      
      // 기타 에러: 안전하게 미제출로 처리
      logger.debug('⚠️ 기타 에러 - 안전하게 미제출로 처리');
      return {
        has_submitted: false,
        can_submit_again: true
      };
    }
  },

  /**
   * 🔥🔥🔥 [신규] 특정 설문 제출 이력 확인 (slug 기반)
   * @param {string} surveySlug - 설문 slug
   * @returns {Promise<Object>} { has_submitted: boolean, can_submit_again: boolean }
   */
  checkSurveySubmission: async (surveySlug) => {
    try {
      const deviceId = await deviceManager.getDeviceId();
      logger.debug('🔍 설문 제출 이력 확인: ' + surveySlug + ' (device: ' + deviceId + ')');
      
      const data = await apiClient.get('/surveys/' + surveySlug + '/check', {
        params: { device_id: deviceId },
        timeout: 5000
      });
      
      const result = {
        has_submitted: data?.has_submitted || false,
        can_submit_again: data?.can_submit_again !== false,
        last_submission: data?.last_submission
      };
      
      logger.debug('✅ 설문 "' + surveySlug + '" 제출 이력:', result);
      return result;
      
    } catch (error) {
      logger.error('❌ 설문 "' + surveySlug + '" 제출 이력 확인 실패:', error);
      
      if (error.response?.status === 404) {
        return { has_submitted: false, can_submit_again: true };
      }
      
      return { has_submitted: false, can_submit_again: true };
    }
  },

  /**
   * 🔥 온보딩 설문 제출 (참고사항 반영)
   * - answer_value 키 사용 (answer_options에서 변경)
   * - device_id 자동 포함
   * @param {Object} answers - { questionId: answer } 형식
   * @returns {Promise<Object>} 제출 결과
   */
  submitOnboardingSurvey: async (answers) => {
    try {
      logger.debug('🚀 온보딩 설문 제출 시작');
      
      const deviceId = await deviceManager.getDeviceId();
      logger.debug('📱 기기 ID:', deviceId);
      
      // 🔥 참고사항 포맷 적용: answer_value 사용
      const formattedAnswers = transformAnswersForApi(answers);
      
      const payload = {
        device_id: deviceId,
        answers: formattedAnswers,
        is_completed: true
      };
      
      logger.debug('📦 API 전송 데이터:', JSON.stringify(payload, null, 2));
      
      const data = await apiClient.post('/surveys/onboarding/submit', payload);
      
      // 🔥🔥🔥 [추가] 응답 데이터 전체 로깅 (탄소 포인트 포함)
      logger.debug('✅ 온보딩 설문 제출 응답:', data);
      
      return data;
      
    } catch (error) {
      logger.error('❌ 설문 제출 실패:', error);
      
      // 에러 메시지 개선
      let errorMessage = '설문 제출에 실패했습니다.';
      
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || '이미 제출된 설문입니다.';
      } else if (error.response?.status === 404) {
        errorMessage = '설문을 찾을 수 없습니다.';
      } else if (error.response?.status === 500) {
        errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      throw new Error(errorMessage);
    }
  },

  /**
   * 일반 설문 조회 (UUID 또는 slug)
   * @param {string} surveyIdentifier - 설문 UUID 또는 slug
   * @returns {Promise<Object>} 설문 데이터
   */
  getSurvey: async (surveyIdentifier) => {
    try {
      logger.debug('📋 설문 조회 중: ' + surveyIdentifier);
      const data = await apiClient.get('/surveys/' + surveyIdentifier);
      logger.debug('✅ 설문 조회 성공:', data?.title || surveyIdentifier);
      return data;
    } catch (error) {
      logger.error('❌ 설문 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 🔥🔥🔥 [핵심 수정] 일반 설문 제출 (UUID 또는 slug)
   * - 백엔드 응답 데이터(carbon_points_earned, carbon_saved_kg, distance_km)를 그대로 반환
   * @param {string} surveyIdentifier - 설문 UUID 또는 slug
   * @param {Object} answers - { questionId: answer } 형식
   * @returns {Promise<Object>} 제출 결과 (탄소 포인트 정보 포함)
   */
  submitSurvey: async (surveyIdentifier, answers) => {
    try {
      logger.debug('🚀 설문 제출: ' + surveyIdentifier);
      
      const deviceId = await deviceManager.getDeviceId();
      logger.debug('📱 기기 ID:', deviceId);
      
      // 🔥🔥🔥 [핵심 수정] transformAnswersForApi 사용
      const formattedAnswers = transformAnswersForApi(answers);
      
      const payload = {
        device_id: deviceId,
        answers: formattedAnswers,
        is_completed: true
      };
      
      logger.debug('📦 API 전송 데이터:', JSON.stringify(payload, null, 2));
      
      const data = await apiClient.post(
        '/surveys/' + surveyIdentifier + '/submit', 
        payload
      );
      
      // 🔥🔥🔥 [핵심 추가] data 객체 전체 반환 (carbon_points_earned 포함됨)
      logger.debug('✅ 설문 제출 응답:', data);
      
      // 🔥 응답 데이터 상세 로깅 (디버깅용)
      if (data) {
        logger.debug('📊 응답 상세:', {
          carbon_points_earned: data.carbon_points_earned,
          carbon_saved_kg: data.carbon_saved_kg,
          distance_km: data.distance_km,
          response_id: data.response_id,
          message: data.message
        });
      }
      
      return data;
      
    } catch (error) {
      logger.error('❌ 설문 제출 실패:', error);
      
      let errorMessage = '설문 제출에 실패했습니다.';
      
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || '이미 제출된 설문입니다.';
      } else if (error.response?.status === 404) {
        errorMessage = '설문을 찾을 수 없습니다.';
      } else if (error.response?.status === 500) {
        errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      throw new Error(errorMessage);
    }
  },

  /**
   * 일반 설문 제출 이력 확인 (device_id 기반)
   * @param {string} surveyIdentifier - 설문 UUID 또는 slug
   * @returns {Promise<boolean>} 제출 여부
   */
  checkSubmissionHistory: async (surveyIdentifier) => {
    try {
      const deviceId = await deviceManager.getDeviceId();
      
      const data = await apiClient.get('/surveys/' + surveyIdentifier + '/check', {
        params: { device_id: deviceId }
      });
      
      return data?.submitted || data?.has_submitted || false;
      
    } catch (error) {
      logger.error('❌ 제출 이력 확인 실패:', error);
      // 에러 발생 시 제출 가능한 것으로 간주
      return false;
    }
  },

  /**
   * 설문 목록 조회
   * @param {Object} filters - 필터 옵션 (예: { is_active: true, survey_type: 'onboarding' })
   * @returns {Promise<Array>} 설문 목록
   */
  listSurveys: async (filters = {}) => {
    try {
      const data = await apiClient.get('/surveys', { params: filters });
      return data;
    } catch (error) {
      logger.error('❌ 설문 목록 조회 실패:', error);
      throw error;
    }
  },
};

export default surveyService;